/**
 * Protocol marketplace data - Available protocols for Evidence.
 *
 * Extracted from shortcut-marketplace-modal.tsx to keep file sizes manageable.
 */

import {
  CATEGORY_ORDER,
  type SuggestedChipCategory,
} from '@/components/ui/transcript-chips/types';
import type { IntlShape, MessageDescriptor } from 'react-intl';
import {
  shortcutMarketplaceBuiltInMessages as M,
  shortcutMarketplaceBrowseFilterMessages,
} from './shortcut-marketplace-data.messages';

// Re-export for backward compatibility
export { CATEGORY_ORDER };

// ============================================================================
// Types
// ============================================================================

/**
 * Which markets a shortcut is available in.
 * - 'all'    – shown everywhere
 * - 'us'     – US only (autonomous Dx/Ix/Mx advice)
 * - 'non_us' – non-US variant (retrieval-oriented rephrasing)
 */
export type ShortcutMarket = 'all' | 'us' | 'non_us';

export interface AvailableShortcut {
  id: string;
  label: MessageDescriptor;
  query: MessageDescriptor;
  category: SuggestedChipCategory;
  description?: MessageDescriptor;
  browseCategory?: BrowseFilterCategory;
  market?: ShortcutMarket;
}

/**
 * Resolved version of AvailableShortcut with plain strings
 * (after running through intl.formatMessage).
 */
export interface ResolvedShortcut {
  id: string;
  label: string;
  query: string;
  category: SuggestedChipCategory;
  description?: string;
  browseCategory?: BrowseFilterCategory;
  market?: ShortcutMarket;
  /** Team shortcut reference id when this shortcut is team-managed. */
  teamShortcutId?: string;
}

export function resolveShortcut(
  shortcut: AvailableShortcut,
  intl: IntlShape
): ResolvedShortcut {
  return {
    id: shortcut.id,
    label: intl.formatMessage(shortcut.label),
    query: intl.formatMessage(shortcut.query),
    category: shortcut.category,
    description: shortcut.description
      ? intl.formatMessage(shortcut.description)
      : undefined,
    browseCategory: shortcut.browseCategory,
    market: shortcut.market,
  };
}

export function resolveShortcuts(
  shortcuts: AvailableShortcut[],
  intl: IntlShape
): ResolvedShortcut[] {
  return shortcuts.map((s) => resolveShortcut(s, intl));
}

// ============================================================================
// Browse Filter Categories
// ============================================================================

export type BrowseFilterCategory =
  | 'all'
  | 'saved'
  | 'my_shortcuts'
  | 'look_up'
  | 'work_up'
  | 'treat'
  | 'prepare'
  | 'write';

export const BROWSE_FILTER_ORDER: BrowseFilterCategory[] = [
  'all',
  'saved',
  'my_shortcuts',
  'look_up',
  'work_up',
  'treat',
  'prepare',
  'write',
];

export const BROWSE_FILTER_MESSAGES: Record<BrowseFilterCategory, MessageDescriptor> = {
  all: shortcutMarketplaceBrowseFilterMessages.filterAll,
  saved: shortcutMarketplaceBrowseFilterMessages.filterSaved,
  my_shortcuts: shortcutMarketplaceBrowseFilterMessages.filterMyShortcuts,
  look_up: shortcutMarketplaceBrowseFilterMessages.filterLookUp,
  work_up: shortcutMarketplaceBrowseFilterMessages.filterWorkUp,
  treat: shortcutMarketplaceBrowseFilterMessages.filterTreat,
  prepare: shortcutMarketplaceBrowseFilterMessages.filterPrepare,
  write: shortcutMarketplaceBrowseFilterMessages.filterWrite,
};

// ============================================================================
// Available Protocols Data
// ============================================================================

export const AVAILABLE_SHORTCUTS: AvailableShortcut[] = [
  // ========================================================================
  // Unchanged -- same in both markets (rows 1-11)
  // ========================================================================
  {
    id: 'default-prep',
    label: M.prep_label,
    query: M.prep_query,
    category: 'suggested_question',
    browseCategory: 'prepare',
    description: M.prep_description,
  },
  {
    id: 'referral-letter',
    label: M.referralLetter_label,
    query: M.referralLetter_query,
    category: 'plan',
    browseCategory: 'write',
    description: M.referralLetter_description,
  },
  {
    id: 'sick-note',
    label: M.sickNote_label,
    query: M.sickNote_query,
    category: 'plan',
    browseCategory: 'write',
    description: M.sickNote_description,
  },
  {
    id: 'patient-letter',
    label: M.patientLetter_label,
    query: M.patientLetter_query,
    category: 'plan',
    browseCategory: 'write',
    description: M.patientLetter_description,
  },
  {
    id: 'translate-spanish',
    label: M.translateSpanish_label,
    query: M.translateSpanish_query,
    category: 'plan',
    browseCategory: 'write',
    description: M.translateSpanish_description,
  },
  {
    id: 'translate-simple',
    label: M.simplify_label,
    query: M.simplify_query,
    category: 'plan',
    browseCategory: 'write',
    description: M.simplify_description,
  },
  {
    id: 'icd-codes',
    label: M.icdCodes_label,
    query: M.icdCodes_query,
    category: 'plan',
    browseCategory: 'prepare',
    description: M.icdCodes_description,
  },
  {
    id: 'billing-summary',
    label: M.billing_label,
    query: M.billing_query,
    category: 'plan',
    browseCategory: 'prepare',
    description: M.billing_description,
  },
  {
    id: 'guideline',
    label: M.guideline_label,
    query: M.guideline_query,
    category: 'suggested_question',
    browseCategory: 'look_up',
    description: M.guideline_description,
  },
  {
    id: 'discharge-summary',
    label: M.dischargeSummary_label,
    query: M.dischargeSummary_query,
    category: 'plan',
    browseCategory: 'write',
    description: M.dischargeSummary_description,
  },
  {
    id: 'clinical-summary',
    label: M.clinicalSummary_label,
    query: M.clinicalSummary_query,
    category: 'suggested_question',
    browseCategory: 'prepare',
    description: M.clinicalSummary_description,
  },

  // ========================================================================
  // Rephrased -- US version (rows 12-20)
  // ========================================================================
  {
    id: 'drug-interactions',
    label: M.drugCheckUs_label,
    query: M.drugCheckUs_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.drugCheckUs_description,
    market: 'us',
  },
  {
    id: 'side-effects',
    label: M.sideEffectsUs_label,
    query: M.sideEffectsUs_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.sideEffectsUs_description,
    market: 'us',
  },
  {
    id: 'check-dosing',
    label: M.checkDosingUs_label,
    query: M.checkDosingUs_query,
    category: 'plan',
    browseCategory: 'look_up',
    description: M.checkDosingUs_description,
    market: 'us',
  },
  {
    id: 'exam',
    label: M.examUs_label,
    query: M.examUs_query,
    category: 'examination',
    browseCategory: 'look_up',
    description: M.examUs_description,
    market: 'us',
  },
  {
    id: 'counselling-points',
    label: M.counsellingPointsUs_label,
    query: M.counsellingPointsUs_query,
    category: 'plan',
    browseCategory: 'prepare',
    description: M.counsellingPointsUs_description,
    market: 'us',
  },
  {
    id: 'safety-net',
    label: M.safetyNetUs_label,
    query: M.safetyNetUs_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.safetyNetUs_description,
    market: 'us',
  },
  {
    id: 'follow-up',
    label: M.followUpUs_label,
    query: M.followUpUs_query,
    category: 'plan',
    browseCategory: 'prepare',
    description: M.followUpUs_description,
    market: 'us',
  },
  {
    id: 'risk-score',
    label: M.riskScoreUs_label,
    query: M.riskScoreUs_query,
    category: 'investigation',
    browseCategory: 'look_up',
    description: M.riskScoreUs_description,
    market: 'us',
  },
  {
    id: 'default-ask',
    label: M.questionsUs_label,
    query: M.questionsUs_query,
    category: 'suggested_question',
    browseCategory: 'look_up',
    description: M.questionsUs_description,
    market: 'us',
  },

  // ========================================================================
  // Rephrased -- Non-US version (rows 12-20)
  // ========================================================================
  {
    id: 'drug-interactions',
    label: M.interactionsNonUs_label,
    query: M.interactionsNonUs_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.interactionsNonUs_description,
    market: 'non_us',
  },
  {
    id: 'side-effects',
    label: M.sideEffectsNonUs_label,
    query: M.sideEffectsNonUs_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.sideEffectsNonUs_description,
    market: 'non_us',
  },
  {
    id: 'check-dosing',
    label: M.dosingNonUs_label,
    query: M.dosingNonUs_query,
    category: 'plan',
    browseCategory: 'look_up',
    description: M.dosingNonUs_description,
    market: 'non_us',
  },
  {
    id: 'exam',
    label: M.examNonUs_label,
    query: M.examNonUs_query,
    category: 'examination',
    browseCategory: 'look_up',
    description: M.examNonUs_description,
    market: 'non_us',
  },
  {
    id: 'counselling-points',
    label: M.patientTalkingPointsNonUs_label,
    query: M.patientTalkingPointsNonUs_query,
    category: 'plan',
    browseCategory: 'prepare',
    description: M.patientTalkingPointsNonUs_description,
    market: 'non_us',
  },
  {
    id: 'safety-net',
    label: M.safetyNettingNonUs_label,
    query: M.safetyNettingNonUs_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.safetyNettingNonUs_description,
    market: 'non_us',
  },
  {
    id: 'follow-up',
    label: M.followUpNonUs_label,
    query: M.followUpNonUs_query,
    category: 'plan',
    browseCategory: 'prepare',
    description: M.followUpNonUs_description,
    market: 'non_us',
  },
  {
    id: 'risk-score',
    label: M.clinicalScoresNonUs_label,
    query: M.clinicalScoresNonUs_query,
    category: 'investigation',
    browseCategory: 'look_up',
    description: M.clinicalScoresNonUs_description,
    market: 'non_us',
  },
  {
    id: 'default-ask',
    label: M.questionsNonUs_label,
    query: M.questionsNonUs_query,
    category: 'suggested_question',
    browseCategory: 'look_up',
    description: M.questionsNonUs_description,
    market: 'non_us',
  },

  // ========================================================================
  // US-only -- removed from non-US (rows 26-33)
  // ========================================================================
  {
    id: 'default-ddx',
    label: M.ddx_label,
    query: M.ddx_query,
    category: 'symptom_exploration',
    browseCategory: 'look_up',
    description: M.ddx_description,
    market: 'us',
  },
  {
    id: 'default-red-flags',
    label: M.redFlags_label,
    query: M.redFlags_query,
    category: 'red_flag',
    browseCategory: 'look_up',
    description: M.redFlags_description,
    market: 'us',
  },
  {
    id: 'default-plan',
    label: M.plan_label,
    query: M.plan_query,
    category: 'plan',
    browseCategory: 'treat',
    description: M.plan_description,
    market: 'us',
  },
  {
    id: 'investigations',
    label: M.tests_label,
    query: M.tests_query,
    category: 'investigation',
    browseCategory: 'work_up',
    description: M.tests_description,
    market: 'us',
  },
  {
    id: 'prescribe',
    label: M.prescribe_label,
    query: M.prescribe_query,
    category: 'plan',
    browseCategory: 'treat',
    description: M.prescribe_description,
    market: 'us',
  },
  {
    id: 'interpret-results',
    label: M.interpretResults_label,
    query: M.interpretResults_query,
    category: 'investigation',
    browseCategory: 'work_up',
    description: M.interpretResults_description,
    market: 'us',
  },
  {
    id: 'compare-treatments',
    label: M.compareTreatments_label,
    query: M.compareTreatments_query,
    category: 'plan',
    browseCategory: 'treat',
    description: M.compareTreatments_description,
    market: 'us',
  },
  {
    id: 'step-up-therapy',
    label: M.stepUpTherapy_label,
    query: M.stepUpTherapy_query,
    category: 'plan',
    browseCategory: 'treat',
    description: M.stepUpTherapy_description,
    market: 'us',
  },
];

/**
 * Return the subset of AVAILABLE_SHORTCUTS visible to a given market.
 * Shortcuts with no `market` field (or market === 'all') are shown everywhere.
 */
export function getShortcutsForMarket(isUS: boolean): AvailableShortcut[] {
  const target: ShortcutMarket = isUS ? 'us' : 'non_us';
  return AVAILABLE_SHORTCUTS.filter(
    (s) => !s.market || s.market === 'all' || s.market === target
  );
}

/**
 * Return resolved shortcuts for a market (convenience wrapper).
 */
export function getResolvedShortcutsForMarket(
  isUS: boolean,
  intl: IntlShape
): ResolvedShortcut[] {
  return resolveShortcuts(getShortcutsForMarket(isUS), intl);
}
